import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { MatterInfo } from './hooks';
import {
  Paper,
  Title,
  Table,
  Text,
  Badge,
  TextInput,
  Group,
  Pagination,
  Select,
  Stack,
  Loader,
} from '@mantine/core';
import { SearchIcon } from 'lucide-react';
import { formatRelativeDate } from './dateUtils';

interface MattersTableProps {
  matters: MatterInfo[];
  loading?: boolean;
}

const MattersTable = ({ matters, loading }: MattersTableProps) => {
  const navigate = useNavigate();
  const { firmId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const handleMatterClick = (matterId: string) => {
    navigate(`/firms/${firmId}/matters/${matterId}`);
  };

  // Filter matters based on search and status
  const filteredMatters = useMemo(() => {
    let filtered = matters;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (matter) =>
          matter.name?.toLowerCase().includes(query) ||
          matter.description?.toLowerCase().includes(query) ||
          matter.id.toLowerCase().includes(query),
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((matter) => matter.status === statusFilter);
    }

    return filtered;
  }, [matters, searchQuery, statusFilter]);

  // Paginate filtered matters
  const paginatedMatters = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredMatters.slice(startIndex, startIndex + pageSize);
  }, [filteredMatters, currentPage]);

  const totalPages = Math.ceil(filteredMatters.length / pageSize);

  // Get unique statuses for filter dropdown
  const statusOptions = useMemo(() => {
    const statuses = Array.from(new Set(matters.map((m) => m.status)));
    return statuses.map((status) => ({
      value: status,
      label: status,
    }));
  }, [matters]);

  // Reset to first page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string | null) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'green';
      case 'closed':
        return 'gray';
      case 'new':
        return 'blue';
      case 'pendingEngagement':
        return 'yellow';
      case 'engagementLetterSent':
        return 'cyan';
      case 'earnedButNotClosed':
        return 'orange';
      case 'rejectedByAttorney':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Paper shadow='sm' p='md' withBorder>
        <Title order={3} mb='md'>
          Matters
        </Title>
        <Group justify='center' p='xl'>
          <Loader color='pink.4' size='lg' type='dots' />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper shadow='sm' p='md' withBorder>
      <Title order={3} mb='md'>
        Matters ({matters.length})
      </Title>

      <Stack gap='md' mb='md'>
        <Group>
          <TextInput
            placeholder='Search by name, description, or ID'
            leftSection={<SearchIcon size={16} />}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder='Filter by status'
            data={statusOptions}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            clearable
            style={{ minWidth: 200 }}
          />
        </Group>

        {filteredMatters.length !== matters.length && (
          <Text size='sm' c='dimmed'>
            Showing {filteredMatters.length} of {matters.length} matters
          </Text>
        )}
      </Stack>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Matter ID</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Matter Type</Table.Th>
            <Table.Th>Practice Area</Table.Th>
            <Table.Th>Responsible Attorney</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Trust Balance</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedMatters.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={8}>
                <Text ta='center' c='dimmed' py='xl'>
                  {searchQuery || statusFilter
                    ? 'No matters match your filters'
                    : 'No matters found'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            paginatedMatters.map((matter) => (
              <Table.Tr
                key={matter.id}
                onClick={() => handleMatterClick(matter.id)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>
                  <Text size='xs' c='dimmed' ff='monospace'>
                    {matter.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size='sm' fw={500}>
                    {matter.name}
                  </Text>
                  {matter.description && (
                    <Text size='xs' c='dimmed' lineClamp={1}>
                      {matter.description}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge size='sm' color={getStatusColor(matter.status)}>
                    {matter.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size='sm'>{matter.matterType || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size='sm'>{matter.practiceArea || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size='sm'>{matter.responsibleAttorney || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  {matter.createdTimestamp ? (
                    <>
                      <Text size='sm'>
                        {formatRelativeDate(matter.createdTimestamp)}
                      </Text>
                      <Text size='xs' c='dimmed'>
                        {new Date(matter.createdTimestamp).toLocaleDateString()}
                      </Text>
                    </>
                  ) : (
                    '—'
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size='sm'>
                    {matter.trustBalance
                      ? `$${matter.trustBalance.toFixed(2)}`
                      : '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify='center' mt='md'>
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
          />
        </Group>
      )}
    </Paper>
  );
};

export default MattersTable;
